import React, { useMemo } from 'react';
import styled from 'styled-components';
import { variables } from '@splunk/themes';
import { selectMode, useSelector } from '@splunk/dashboard-state';
import type { Mode, VizContainerOptions } from '@splunk/dashboard-types';
import { sanitizeColor } from '../utils/colorUtils';
import { customThemeVariables } from '../customThemeVariables';

export const TITLE_CONTAINER_PADDING = 12;
export const TITLE_HEIGHT = 20;
export const DESCRIPTION_HEIGHT = 16;

type TitleProps = {
    options?: VizContainerOptions;
};

const Title = styled.div.attrs(() => ({
    'data-test': 'viz-title',
}))<TitleProps>`
    flex-basis: auto;
    font-size: 14px;
    font-weight: 600;
    line-height: ${TITLE_HEIGHT}px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: ${variables.contentColorActive};
    color: ${(props) =>
        props.options?.title?.color
            ? sanitizeColor(props.options?.title?.color)
            : variables.contentColorActive};
`;

type DescriptionProps = {
    noTitle?: boolean;
    options: VizContainerOptions;
};

const Description = styled.div.attrs(() => ({
    'data-test': 'viz-description',
}))<DescriptionProps>`
    flex-basis: auto;
    font-size: 12px;
    line-height: ${DESCRIPTION_HEIGHT}px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: ${variables.contentColorActive};
    color: ${(props) =>
        props.options?.description?.color
            ? sanitizeColor(props.options?.description?.color)
            : variables.contentColorActive};
    opacity: 0.7;
    /* offset top margin when there is no title so description lines up with other titles */
    margin-top: ${({ noTitle }) => (noTitle ? '3px' : 'unset')};
`;

type ContainerProps = {
    backgroundColor?: string | null;
    mode: Mode;
};

const Container = styled.div.attrs(() => ({
    'data-test': 'viz-title-description-container',
}))<ContainerProps>`
    padding: ${TITLE_CONTAINER_PADDING / 2}px 10px;
    background-color: ${(props) =>
        props.backgroundColor || customThemeVariables.vizPanelBackgroundColor};
    /* Fix sub-pixel gaps between elements when scaled */
    box-shadow: 0 1px 1px 0
        ${(props) =>
            props.backgroundColor ||
            customThemeVariables.vizPanelBackgroundColor};
    overflow: hidden;
    user-select: ${({ mode }) => mode === 'edit' && 'none'};
`;

const TextWithStatusAllotment = styled.div.attrs(() => ({
    'data-test': 'viz-title-description-status-padding',
}))<Record<string, unknown>>`
    width: calc(100% - 12px);
`;

interface TitleAndDescriptionProps {
    description?: string;
    title?: string;
    backgroundColor?: string | null;
    truncateFirstLine?: boolean;
    containerOptions?: VizContainerOptions;
}

const defaultContainerOptions = Object.freeze({});

/**
 * Renders a container for a visualization title and description
 * @param {Object} param0 VizTitleAndDescription parameters
 * @param {string | undefined} [description] Visualization description to be rendered
 * @param {string | undefined} [title] Visualization title to be rendered
 * @param {string | null | undefined} [backgroundColor] Background color to fill the container and for box shadows. Will default to `variables.backgroundColorSection`
 * @param {boolean | undefined} [truncateFirstLine] Indicate that the first rendered line - either title or description - should contain padding to prevent rendering in same space as a viz status icon
 * @returns A rendered container with the title and/or description, else null
 */
const VizTitleAndDescription = ({
    description,
    title,
    backgroundColor,
    truncateFirstLine,
    containerOptions = defaultContainerOptions,
}: TitleAndDescriptionProps): JSX.Element | null => {
    const mode = useSelector(selectMode);

    const TitleMemo = useMemo(() => {
        if (!title) {
            return null;
        }

        if (truncateFirstLine) {
            return (
                <TextWithStatusAllotment>
                    <Title>{title}</Title>
                </TextWithStatusAllotment>
            );
        }

        return <Title options={containerOptions}>{title}</Title>;
    }, [title, truncateFirstLine, containerOptions]);

    // If there's no title then the description may need to be truncated to
    // prevent rendering text on top of the status icon (when it's present)
    const DescriptionMemo = useMemo(() => {
        if (!description) {
            return null;
        }

        // If there's a title or truncateFirstLine is false then there's no
        // need to render the text in a TextWithStatusAllotment container
        if (title || !truncateFirstLine) {
            return (
                <Description options={containerOptions}>
                    {description}
                </Description>
            );
        }

        return (
            <TextWithStatusAllotment>
                <Description noTitle options={containerOptions}>
                    {description}
                </Description>
            </TextWithStatusAllotment>
        );
    }, [description, title, truncateFirstLine, containerOptions]);

    // Cannot have conditional hook execution. This check needs to come after useMemo
    if (!title && !description) {
        return null;
    }

    return (
        <Container backgroundColor={backgroundColor} mode={mode}>
            {TitleMemo}
            {DescriptionMemo}
        </Container>
    );
};

export default VizTitleAndDescription;
